import { InjectionToken } from '@angular/core';
import { jwtTokenStorage } from '@skyeng/libs/vimbox-core/user';
import { type Observable } from 'rxjs';

import { type ReportChatAttachment } from '../attachments';
import { type ReportChatMessage } from '../interfaces';

/**
 * Base HTTP(S) URL of the speech-recognition proxy, e.g.
 * `https://edu-avatar-speech-recognition-proxy.<rootDomain>`. The socket
 * service rewrites the scheme to `wss` and appends `/ws/report-chat/<id>`.
 * Each consuming app binds `environment.endpoints.speechRecognitionProxy`.
 */
export const REPORT_CHAT_SOCKET_BASE_URL = new InjectionToken<string>('REPORT_CHAT_SOCKET_BASE_URL');

/**
 * Auth subprotocol scheme. A browser WebSocket cannot set an Authorization
 * header, and a JWT carrying many roles overflows the per-cookie size limit (it
 * then lives only in localStorage and is never sent as a cookie). The client
 * therefore offers `[REPORT_CHAT_BEARER_SUBPROTOCOL, <jwt>]` as
 * `Sec-WebSocket-Protocol`; the proxy reads the token from the second value and
 * echoes the scheme. Mirrors `_BEARER_SUBPROTOCOL` in the proxy WS route.
 */
export const REPORT_CHAT_BEARER_SUBPROTOCOL = 'bearer';

/**
 * Resolves the JWT used to authorize the chat socket — the cookie when it fits,
 * else localStorage (a many-roles JWT overflows the cookie). Defaults to the
 * shared `jwtTokenStorage` (cookie-or-localStorage), the same source the report
 * submission API already uses; overridable in tests.
 */
export const REPORT_CHAT_AUTH_TOKEN = new InjectionToken<() => string | undefined>('REPORT_CHAT_AUTH_TOKEN', {
  providedIn: 'root',
  factory: () => () => jwtTokenStorage.getToken(),
});

/**
 * Connection lifecycle as surfaced to the host containers. `unauthorized` is a
 * terminal auth/permission close (4401/4403) — distinct from `closed` (transport
 * loss / reconnect exhausted) so the UI does not mislabel an auth problem as a
 * dropped connection.
 */
export type ReportChatSocketStatus = 'connecting' | 'open' | 'reconnecting' | 'closed' | 'unauthorized';

/**
 * Author projection the viewer represents. Drives the optimistic `authorRole`
 * (student outbound vs. staff outbound) — the server still derives the real
 * role from the JWT, this only colours the optimistic bubble before the echo.
 */
export type ReportChatSocketViewerRole = 'student' | 'staff';

/** Options for {@link ReportChatSocketService.connect}. */
export interface ReportChatConnectOptions {
  /**
   * Last message id the caller already holds. Forwarded as `?after=<id>` so the
   * server replays only the catch-up tail (with a small overlap) on connect.
   */
  afterId?: number;
  /**
   * Which side the viewer is on. Only affects the optimistic bubble's
   * `authorRole`/`mine` before the server echo arrives. Defaults to `student`.
   */
  viewerRole?: ReportChatSocketViewerRole;
}

/**
 * Server → client frame. Two `message` projections share the shape (student
 * omits `authorUserId`, staff omits `seenByStaff`); `ack`/`error` carry the
 * client `tempId` for optimistic reconciliation.
 */
export type ReportChatServerFrame =
  | {
      type: 'message';
      reportId: number;
      id: number;
      // `system` — a server-generated notice (e.g. status change); its `body` is
      // a JSON `{key, params}` i18n payload the host localizes, not plain text.
      authorRole: 'student' | 'staff' | 'system';
      body: string;
      createdAt: string;
      mine: boolean;
      authorUserId?: number;
      seenByStaff?: boolean;
      // Files sent with the message. Absent on servers that predate the
      // attachment contract and on messages sent without one — both are
      // "no attachments", never an error.
      attachments?: ReportChatAttachment[];
    }
  | { type: 'ack'; reportId: number; tempId: string; id: number }
  | { type: 'error'; code: number; message: string; tempId?: string }
  // Sent once on connect: the viewer's read cursor (highest message id already
  // read) as it stood BEFORE this session. `null` = nothing read yet. The client
  // anchors the "new messages" divider at the first message with a greater id.
  | { type: 'read_state'; cursor: number | null }
  // Pushed when the report's LLM title becomes available (generated async after
  // submit). Lets the open chat / history card reflect it live.
  | { type: 'title'; reportId: number; title: string };

/**
 * Client → server send frame. `attachments` is written ONLY when the message
 * actually carries files: a plain text send stays byte-identical to the
 * pre-attachment frame, so it keeps working against a server that has not
 * shipped the field yet.
 */
export interface ReportChatClientFrame {
  tempId: string;
  body: string;
  attachments?: ReportChatAttachment[];
}

/**
 * A thread-level error not tied to a specific optimistic send — e.g. the
 * claim-on-reply conflict (403 not-your-report / 409 claimed-by-another) the
 * server emits as a tempId-less `error` frame. Surfaced so the operator UI can
 * react instead of silently swallowing it.
 */
export interface ReportChatSessionError {
  code: number;
  message: string;
}

/** A live per-report chat session. Close it to tear the socket down. */
export interface ReportChatSession {
  /** Sorted ASC by id, deduped by id, optimistic rows appended after server rows. */
  readonly messages$: Observable<ReportChatMessage[]>;
  readonly status$: Observable<ReportChatSocketStatus>;
  /** Thread-level (tempId-less) errors — e.g. claim 403/409. */
  readonly errors$: Observable<ReportChatSessionError>;
  /**
   * Whether the LATEST send is in a failed state — the signal the host
   * containers use to raise the global "send failed" banner.
   *
   * Tracks ONLY the most recent send (by its `tempId`), not "any failed row in
   * the thread": it resets to `false` on every new `send`/`retry`, flips to
   * `true` only when that exact send errors or its socket drops mid-flight, and
   * clears back to `false` when that send acks. An older failed bubble earlier
   * in the history therefore no longer pins the banner forever — the regression
   * this fixes versus the HTTP version.
   */
  readonly sendFailed$: Observable<boolean>;
  /**
   * The viewer's read cursor from the server's `read_state` frame — the highest
   * message id already read BEFORE this session (`null` = nothing read). The host
   * anchors the "new messages" divider at the first message with a greater id and
   * freezes it for the session.
   */
  readonly readCursor$: Observable<number | null>;
  /**
   * The report's LLM-generated title, pushed once it is ready (async after
   * submit). Emits only on a real `title` frame; the host uses it to update the
   * report's history card / header live instead of waiting for a refetch.
   */
  readonly reportTitle$: Observable<string>;
  /**
   * Send a message, optionally with already-uploaded files. Uploading is the
   * host's job — the session only carries the resulting `{url, filename}` list.
   * An attachment-only send (empty body) is valid.
   */
  send(body: string, attachments?: ReportChatAttachment[]): void;
  retry(message: ReportChatMessage): void;
  /**
   * Advance the viewer's read cursor to `upToId` (the highest message id it has
   * now seen — the host calls this when the thread reaches the bottom). Best-effort
   * fire-and-forget; the server keeps it monotonic.
   */
  markRead(upToId: number): void;
  close(): void;
}
